// Builds, per row, a plan describing what saving it should do. Pure/synchronous so it can be
// unit-tested; the actual HTTP is executed by the view (which owns the http helpers + state).
//
// ID semantics (see project memory): editing the ID column does NOT rename the row's entry. It
// retargets the write to a different entry - updating it if it exists, creating it if not - and
// leaves the original entry untouched. New rows create (blank ID) or upsert (typed ID).

import { decodeCell } from './codec'
import { fieldDefFor } from './columns'
import { dirtyKey } from './cell'
import { isEmptyRteValue } from '../../utils'

export type CellError = { columnKey: string, label: string, message: string }

export type RowPlan = {
	rowId: string            // the grid row identity (real id, or local id for new rows)
	isNew: boolean
	op: 'update' | 'create' | 'upsert'
	targetId: string         // id to write to ('' => create with a generated id)
	contentTypeId: string | null
	fields: any              // assembled field payload (pre-cleanup)
	cellErrors: CellError[]  // per-cell decode failures (block the row's save)
	rowError: string | null  // row-level validation error (blocks the row's save)
	hasWork: boolean         // whether this row should be saved at all
}

// the id this row will actually write to: the typed ID override if any, else the row's own id
// (blank for a new row => generate)
export function effectiveId(row: any, dirty: Record<string, string>): string {
	const k = dirtyKey(row.sys.id, 'sys.id')
	const raw = dirty[k] !== undefined ? dirty[k] : (row.sys?._new ? '' : row.sys.id)
	return (raw ?? '').trim()
}

// strip empty locale values (mirrors EntryApiMixin.saveEntry) so we don't persist blanks
export function cleanFields(fields: any): any {
	const out = JSON.parse(JSON.stringify(fields ?? {}))
	for (const f in out) {
		const field = out[f]
		for (const l in field ?? {}) {
			const v = field[l]
			if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0) || isEmptyRteValue(v))
				delete field[l]
		}
		if (field && Object.keys(field).length === 0) delete out[f]
	}
	return out
}

export function buildRowPlan(row: any, columns: any[], dirty: Record<string, string>, typeLookup: any): RowPlan {
	const rowId = row.sys.id
	const isNew = !!row.sys?._new
	const contentTypeId = row.sys?.contentType?.sys?.id ?? null
	const targetId = effectiveId(row, dirty)
	const idChanged = dirty[dirtyKey(rowId, 'sys.id')] !== undefined
	const cellErrors: CellError[] = []
	let rowError: string | null = null

	// base = existing field values (loaded rows) or empty (new rows), then apply decoded edits
	const fields = isNew ? {} : JSON.parse(JSON.stringify(row.fields ?? {}))
	let fieldChanges = 0
	for (const column of columns) {
		if (column.kind !== 'field') continue
		const key = dirtyKey(rowId, column.key)
		if (dirty[key] === undefined) continue
		const field = fieldDefFor(typeLookup, row, column.fieldId)
		if (!field) continue // column not applicable to this row's content type
		fieldChanges++
		try {
			const value = decodeCell(dirty[key], field)
			if (!fields[column.fieldId]) fields[column.fieldId] = {}
			if (value === undefined) delete fields[column.fieldId][column.locale]
			else fields[column.fieldId][column.locale] = value
		}
		catch (e: any) {
			cellErrors.push({ columnKey: column.key, label: column.label, message: e?.message ?? String(e) })
		}
	}

	let op: RowPlan['op']
	if (isNew) {
		if (!contentTypeId) rowError = 'Select a content type'
		op = targetId === '' ? 'create' : 'upsert'
	}
	else if (targetId === '') {
		rowError = 'ID cannot be empty'
		op = 'update'
	}
	else if (targetId === rowId) {
		op = 'update'
	}
	else {
		op = 'upsert' // retarget to a different entry
	}

	const hasWork = isNew ? !!contentTypeId : (fieldChanges > 0 || idChanged)
	return { rowId, isNew, op, targetId, contentTypeId, fields, cellErrors, rowError, hasWork }
}
